import { useState } from 'react';
import { Bell, User, Clock, CheckCircle2, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useAssistanceRequests,
  AssistanceRequest,
  ASSISTANCE_REASONS,
} from '@/hooks/useAssistanceRequests';
import { cn } from '@/lib/utils';

interface AssistanceNotificationBannerProps {
  className?: string;
}

export function AssistanceNotificationBanner({ className }: AssistanceNotificationBannerProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { pendingRequests, acknowledgeRequest, resolveRequest, getTimeAgo } =
    useAssistanceRequests();

  if (pendingRequests.length === 0) return null;

  // Get the most urgent (oldest) request
  const urgentRequest = pendingRequests[0];
  const reasonInfo = ASSISTANCE_REASONS[urgentRequest.reason];

  const handleAcknowledge = async (request: AssistanceRequest) => {
    try {
      await acknowledgeRequest.mutateAsync(request.id);
    } catch {
      // Error handled in hook
    }
  };

  const handleResolve = async (request: AssistanceRequest) => {
    try {
      await resolveRequest.mutateAsync(request.id);
    } catch {
      // Error handled in hook
    }
  };

  return (
    <div
      className={cn(
        'mb-6 rounded-xl border-2 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 overflow-hidden',
        className
      )}
    >
      {/* Main banner */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="relative">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50">
            <Bell className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">
            {pendingRequests.length}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-red-800 dark:text-red-200">
            {pendingRequests.length === 1
              ? 'Assistance Requested'
              : `${pendingRequests.length} Assistance Requests`}
          </p>
          <p className="text-sm text-red-700 dark:text-red-300 truncate">
            {urgentRequest.requester_name} at {urgentRequest.register_name || 'Unknown Register'} -{' '}
            {reasonInfo?.label}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700"
          >
            <Clock className="mr-1 h-3 w-3" />
            {getTimeAgo(urgentRequest.created_at)}
          </Badge>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-red-600 dark:text-red-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-red-600 dark:text-red-400" />
            )}
          </Button>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-red-200 dark:border-red-800 bg-white/50 dark:bg-black/20">
          <div className="max-h-64 overflow-y-auto">
            {pendingRequests.map((request) => {
              const reqReasonInfo = ASSISTANCE_REASONS[request.reason];
              const isPending = request.status === 'pending';

              return (
                <div
                  key={request.id}
                  className="flex items-center gap-3 px-4 py-3 border-b border-red-100 dark:border-red-900/50 last:border-b-0"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                    <User className="h-4 w-4 text-muted-foreground" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{request.requester_name}</p>
                      {request.register_name && (
                        <Badge variant="secondary" className="text-xs">
                          {request.register_name}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-sm text-muted-foreground">{reqReasonInfo?.label}</p>
                      <span className="text-muted-foreground">•</span>
                      <p className="text-xs text-muted-foreground">
                        {getTimeAgo(request.created_at)}
                      </p>
                    </div>
                    {request.notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        "{request.notes}"
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {isPending ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcknowledge(request);
                          }}
                          disabled={acknowledgeRequest.isPending}
                        >
                          {acknowledgeRequest.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Acknowledge'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolve(request);
                          }}
                          disabled={resolveRequest.isPending}
                        >
                          {resolveRequest.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle2 className="mr-1 h-4 w-4" />
                              Resolve
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge
                          variant="outline"
                          className="bg-blue-100 text-blue-800 border-blue-300"
                        >
                          Acknowledged
                        </Badge>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolve(request);
                          }}
                          disabled={resolveRequest.isPending}
                        >
                          {resolveRequest.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle2 className="mr-1 h-4 w-4" />
                              Resolve
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
